import { Outlet, createFileRoute, isRedirect, redirect } from "@tanstack/react-router";

import { AppShell } from "@/components/AppShell";
import { sessionQuery } from "@/features/auth/queries";
import { ApiError } from "@/lib/api/problem";
import { setLocale } from "@/lib/i18n";
import { currentReturnPath } from "@/lib/redirect";

/**
 * The session gate. A pathless layout, so every route beneath it is
 * behind the check without each one remembering to ask.
 *
 * **This is not a security boundary.** It decides what to render, not what
 * is allowed — the server refuses every request this gate would have
 * blocked, and the E2E suite navigates straight to a hidden route to prove
 * it. What the gate buys is not showing a signed-out person a dashboard
 * frame that then fails to fill in.
 */
export const Route = createFileRoute("/_authed")({
  beforeLoad: async ({ context, location }) => {
    try {
      // `ensureQueryData`, not a fetch: the loader primes the cache and
      // every component below reads it synchronously. Routes fetch nothing
      // else themselves.
      const me = await context.queryClient.ensureQueryData(sessionQuery);
      // The session's language, applied **before** the tree renders rather
      // than from an effect after it.
      //
      // Doing this in `AppShell`'s `useEffect` left the chrome in English
      // for a Hindi session — nav, breadcrumbs and any component with no
      // other reason to re-render — while `<html lang>` said `hi`, which
      // is worse than either alone: a screen reader reads English words
      // with Hindi pronunciation. The locale bundles are imported, not
      // fetched, so `changeLanguage` settles almost immediately and can
      // emit `languageChanged` before react-i18next's subscribers have
      // attached, leaving them rendered in the previous language forever.
      // Switching locale from the menu *after* mount always worked, which
      // is why this survived: it only ever showed up on first paint.
      //
      // Setting it here removes the race rather than papering over it —
      // by the time any component renders, i18next is already right, so
      // nothing has to re-render and there is no flash of English either.
      await setLocale(me.resolved_locale);
      // Anyone not yet activated gets one screen, whichever route they
      // asked for — every other page would render an empty frame around a
      // series of 403s.
      if (me.status !== "active" && location.pathname !== "/pending-activation") {
        throw redirect({ to: "/pending-activation" });
      }
    } catch (error) {
      // A `redirect` is thrown, so it arrives here too. Rethrowing it
      // unexamined would be caught by the 401 branch below and turned into
      // a trip to the login page.
      if (isRedirect(error)) throw error;
      // 401 only. A 503 (the revocation list is unreachable and auth is
      // failing closed) must not bounce the user to an IdP that cannot
      // help — that turns a transient outage into a redirect loop.
      if (error instanceof ApiError && error.status === 401) {
        throw redirect({ to: "/login", search: { redirect: currentReturnPath() } });
      }
      throw error;
    }
  },
  component: AuthedLayout,
});

function AuthedLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
